import { motion } from 'motion/react'
import React, { useEffect, useRef, useState } from 'react'
import { BrowserRouter, useSearchParams } from 'react-router'

import { LogoWithName } from 'apps/components/toolbar/LogoWithName'
import LoadingSpinnerCentered from 'components/reusable/LoadingSpinnerCentered'
import { Modal } from 'slab'
import {
  useGetPartnersAdpEmbeddedJobIdStatus,
  usePostPartnersAdpEmbeddedImportProspect,
  usePostPartnersAdpEmbeddedJobIdOnboardingSession
} from 'utils/web_api'
import { type WebAPIEntitiesAdpJob, WebAPIEntitiesAdpJobErrorType, type WebAPIEntitiesAdpProspectImportResponse } from 'utils/web_api.schemas'

const INITIAL_DELAY_MS = 750
const BETWEEN_DELAY_MS = 1250
const DURATION = 0.8

const variants = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
  }
}

type ADPEmbeddedPageProps = {
  targetOrigins: string[]
  loginUrl: string
}

const ADPEmbeddedPageImpl: React.FC<ADPEmbeddedPageProps> = ({ targetOrigins, loginUrl }) => {
  const [jobId, setJobId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showDelayedContent, setShowDelayedContent] = useState(false)
  const [searchParams] = useSearchParams()
  const jwtTokenRef = useRef<string | null>(null)
  const exchangeStartedRef = useRef(false)

  const importProspectMutation = usePostPartnersAdpEmbeddedImportProspect({
    mutation: {
      onSuccess: (data: WebAPIEntitiesAdpProspectImportResponse) => {
        if (data.success && data.job?.id) {
          setJobId(data.job.id)
        } else {
          setError('Failed to start ADP import: No job created')
        }
      },
      onError: (error: any) => {
        console.error('Error importing prospect:', error)
        setError('Failed to start ADP import. Please try again.') // TODO: Revisit user facing error message
      }
    }
  })

  // Poll for job status when we have a job ID
  const jobStatusQuery = useGetPartnersAdpEmbeddedJobIdStatus(
    jobId!,
    {
      query: {
        enabled: !!jobId,
        refetchInterval: (query) => {
          const job = query.state.data as WebAPIEntitiesAdpJob | undefined
          if (['complete', 'error'].includes(job?.state ?? '')) {
            return false // Stop polling
          }
          return 1000 // Poll every 1000ms
        }
      },
      request: jwtTokenRef.current
        ? {
            headers: {
              Authorization: 'Bearer ' + jwtTokenRef.current
            }
          }
        : undefined
    }
  )

  useEffect(() => {
    // Show delayed content
    const delayTimer = setTimeout(() => {
      setShowDelayedContent(true)
    }, INITIAL_DELAY_MS + BETWEEN_DELAY_MS)

    // Extract token from URL query parameters
    const jwtToken = searchParams.get('token')

    if (!jwtToken) {
      setError('No JWT token provided in query parameters')
      return
    }

    jwtTokenRef.current = jwtToken

    // Trigger the mutation to import the prospect
    importProspectMutation.mutate({
      data: { token: jwtToken }
    })

    return () => {
      clearTimeout(delayTimer)
    }
  }, [])

  const isInitializing = importProspectMutation.isPending
  const job = jobStatusQuery.data
  const jobState = job?.state

  // Mutation to exchange a completed job + JWT for an onboarding redirect URL
  const exchangeSessionMutation = usePostPartnersAdpEmbeddedJobIdOnboardingSession()

  // Wrapper to keep side effects (navigation) outside the mutation hook configuration
  const requestOnboardingRedirect = (jobId: string, jwt: string) => {
    if (exchangeStartedRef.current) return
    exchangeStartedRef.current = true

    exchangeSessionMutation.mutate(
      { jobId, data: { token: jwt } },
      {
        onSuccess: (resp) => {
          if (resp.redirect_url) {
            window.location.assign(resp.redirect_url)
          } else {
            setError('Failed to get onboarding redirect URL')
            exchangeStartedRef.current = false
          }
        },
        onError: () => {
          setError('Failed to create onboarding session. Please try again.')
          exchangeStartedRef.current = false
        }
      }
    )
  }

  const isInIframe = () => {
    try {
      return window.self !== window.top
    } catch {
      return true
    }
  }

  // When the job completes, exchange for a redirect URL
  useEffect(() => {
    if (jobState === 'complete' && jobId && jwtTokenRef.current) {
      requestOnboardingRedirect(jobId, jwtTokenRef.current)
    }
  }, [jobState, jobId])

  // Determine current status
  const getStatusInfo = () => {
    if (error) {
      return {
        type: 'error' as const,
        message: error,
        loading: false
      }
    }

    if (isInitializing) {
      return {
        type: 'loading' as const,
        message: 'Starting ADP import...',
        loading: true
      }
    }

    if (!jobId) {
      return {
        type: 'loading' as const,
        message: 'Initializing...',
        loading: true
      }
    }

    switch (jobState) {
      case 'complete':
        return {
          type: 'success' as const,
          message: 'ADP integration completed successfully!',
          loading: false
        }
      case 'error':
        return {
          type: 'error' as const,
          message: 'ADP integration failed. Please try again.',
          loading: false
        }
      case 'in_progress':
        return {
          type: 'loading' as const,
          message: 'Processing ADP integration...',
          loading: true
        }
      case 'pending':
      default:
        return {
          type: 'loading' as const,
          message: 'ADP integration in progress...',
          loading: true
        }
    }
  }

  const status = getStatusInfo()
  const success = status.type === 'success'
  const isDirectLoginConflict = jobState === 'error' && job?.error_type === WebAPIEntitiesAdpJobErrorType.existing_thatch_user_requires_direct_login

  return (
    <div className='min-h-screen flex flex-col items-center justify-center'>
      <motion.div
        variants={variants}
        initial='hidden'
        animate='visible'
        transition={{
          delay: INITIAL_DELAY_MS / 1000,
          duration: DURATION
        }}
      >
        <LogoWithName scale={1.6} />
      </motion.div>
      {showDelayedContent &&
        <>
          <motion.h2
            id='delayed-header'
            variants={variants}
            initial='hidden'
            animate='visible'
            transition={{
              duration: DURATION,
              ease: 'easeOut'
            }}
            className='text-xl mt-12 mb-8'
          >
            Modern benefits that are predictable for you, personal for your employees
          </motion.h2>
          <motion.div
            id='delayed-state'
            variants={variants}
            initial='hidden'
            animate='visible'
            transition={{
              delay: BETWEEN_DELAY_MS / 1000,
              duration: DURATION
            }}
            className='flex flex-col items-center justify-center' style={{ animationDelay: `${BETWEEN_DELAY_MS / 1000}s` }}
          >
            {status.loading
              ? (
                <>
                  <h3 className='mb-4 text-gray-500'>Fetching your employee information from ADP Run...</h3>
                  <LoadingSpinnerCentered width='64' height='64' />
                </>
                )
              : (
                <>
                  <div className='mx-auto flex flex-row items-center justify-center gap-4 mb-4'>
                    <div className={`flex items-center justify-center h-12 w-12 rounded-full ${success ? 'bg-green-100' : 'bg-red-100'}`}>
                      {success
                        ? (
                          <svg className='h-6 w-6 text-green-600' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' />
                          </svg>
                          )
                        : (
                          <svg className='h-6 w-6 text-red-600' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
                          </svg>
                          )}
                    </div>
                    <h2 className='text-xl font-semibold text-gray-900'>
                      ADP connection {success ? 'successful' : 'failed'}
                    </h2>
                  </div>
                </>
                )}
          </motion.div>
        </>}
      <Modal open={isDirectLoginConflict} aria-describedby={undefined}>
        <Modal.Body>
          <Modal.Title dismissible={false}>Log in to Thatch to continue</Modal.Title>
          <div className='mt-4 space-y-4 body'>
            <p>
              It looks like you already created a Thatch account directly. To finish setting up your benefits, please log in to your Thatch account.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className='flex flex-col gap-2'>
            <Modal.Button
              variant='primary'
              onClick={() => {
                if (isInIframe()) {
                  const message = {
                    type: 'THATCH_REDIRECT',
                    version: 1,
                    payload: {
                      redirectUrl: loginUrl,
                      expiresAt: null,
                    },
                  }
                  for (const origin of targetOrigins) {
                    window.parent?.postMessage(message, origin)
                  }
                } else {
                  window.location.assign(loginUrl)
                }
              }}
              width='full'
            >
              Go to thatch.com to log in
            </Modal.Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

const ADPEmbeddedPage: React.FC<ADPEmbeddedPageProps> = ({ targetOrigins, loginUrl }) => {
  return (
    <BrowserRouter>
      <ADPEmbeddedPageImpl targetOrigins={targetOrigins} loginUrl={loginUrl} />
    </BrowserRouter>
  )
}

export default ADPEmbeddedPage
